import moment, { Moment } from "moment";
import i18n from "locales/i18n";
import { CustomEntry } from "./types";
import { DATE_FORMAT, TIME_FORMAT } from "./constants";

const tc = i18n.getFixedT(null, "calendar");

export const genId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ---- time <-> moment (for DesktopTimePicker) ----
export const timeToMoment = (time?: string): Moment | null =>
  time ? moment(time, TIME_FORMAT) : null;

export const momentToTime = (m: Moment | null): string =>
  m && m.isValid() ? m.format(TIME_FORMAT) : "";

// ---- date <-> moment (for DatePicker) ----
export const dateToMoment = (date?: string): Moment | null =>
  date ? moment(date, DATE_FORMAT) : null;

export const momentToDate = (m: Moment | null): string =>
  m && m.isValid() ? m.format(DATE_FORMAT) : "";

// ---- display formatting ----
export const formatTimeLabel = (time?: string): string =>
  time ? moment(time, TIME_FORMAT).format("h:mm A") : "";

export const formatDateLabel = (date?: string): string =>
  date ? moment(date, DATE_FORMAT).format("MMM D, YYYY (ddd)") : "";

export const formatDateShort = (date?: string): string =>
  date ? moment(date, DATE_FORMAT).format("MMM D, YYYY") : "";

// Inclusive list of ISO dates between start and end.
export const enumerateDates = (start: string, end: string): string[] => {
  const result: string[] = [];
  const cursor = moment(start, DATE_FORMAT);
  const last = moment(end, DATE_FORMAT);
  while (cursor.isSameOrBefore(last, "day")) {
    result.push(cursor.format(DATE_FORMAT));
    cursor.add(1, "day");
  }
  return result;
};

// One-line title + subtitle for a block-off list row.
export const summarizeEntry = (
  entry: CustomEntry,
): { title: string; subtitle: string } => {
  const note = entry.note ? ` (${entry.note})` : "";

  if (entry.mode === "single") {
    const title = `${formatDateShort(entry.date)}${note}`;
    const subtitle =
      entry.availability === "unavailable"
        ? tc("availability.unavailable")
        : `${formatTimeLabel(entry.from)} - ${formatTimeLabel(entry.to)}`;
    return { title, subtitle };
  }

  const title = `${formatDateShort(entry.startDate)} – ${formatDateShort(
    entry.endDate,
  )}${note}`;
  let subtitle = tc("availability.unavailable");
  if (entry.availability === "sameHours") {
    subtitle = `${formatTimeLabel(entry.from)} - ${formatTimeLabel(entry.to)}`;
  } else if (entry.availability === "perDay") {
    subtitle = tc("availability.custom_hours");
  }
  return { title, subtitle };
};
